import { ArrowRight, Check } from "lucide-react";
import AbstractShape from "@/components/AbstractShape";
import { goToIntake, goToProductIntake } from "@/lib/intake";
import useSEO from "@/lib/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

const WELLNESS_TIERS = [
  {
    name: "Wellness Injection Starter",
    desc: "An entry-level wellness injection program for routine support.",
    points: [
      "Single category program",
      "Routine clinician review",
      "Pharmacy fulfillment when prescribed",
    ],
  },
  {
    name: "Wellness Injection Value Pack",
    desc: "Multi-month wellness support with simplified ongoing access.",
    points: [
      "Multi-month supply",
      "Continued clinical appropriateness review",
      "Pharmacy fulfillment when prescribed",
    ],
    featured: true,
  },
];

const GLP1_OPTIONS = [
  { name: "Compounded Semaglutide", price: 159 },
  { name: "Compounded Tirzepatide", price: 219 },
];

const FLAT_RATE_NOTE =
  "Flat-rate pricing. Your monthly price never increases as your dose increases.";

export default function Pricing() {
  useSEO({
    title: "Melbi Pricing | Transparent, Flat-Rate Wellness Care",
    description:
      "Flat-rate, no-insurance pricing for clinician-guided wellness. Compounded Semaglutide from $159/mo, Compounded Tirzepatide from $219/mo. Prescription required.",
    url: "https://melbiwellness.com/pricing",
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Pricing", url: "/pricing" },
      ]),
    ],
  });
  return (
    <div data-testid="pricing-page">
      <section className="relative">
        <AbstractShape
          variant="gradient-warm"
          colors={["#F5E6D3", "#E8D5C4", "#D4DCD7"]}
          className="absolute inset-0"
          testId="pricing-hero-bg"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Pricing
          </p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#2C2B29] max-w-3xl">
            Transparent, no insurance needed.
          </h1>
          <p className="mt-5 text-[#5E5C58] text-lg leading-relaxed max-w-2xl">
            Direct-pay, clinician-guided care. Start your intake to see full
            treatment options and pricing for your plan.
          </p>
        </div>
      </section>

      {/* WELLNESS PRICING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Wellness injections
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]">
            Energy · Antioxidant · Vitality · Metabolic Support · Recovery · NAD+ Nasal Spray
          </h2>
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {WELLNESS_TIERS.map((tier) => (
            <div
              key={tier.name}
              data-testid={`pricing-tier-${tier.name.toLowerCase().replace(/\s/g, "-")}`}
              className={`relative rounded-3xl p-6 md:p-8 border transition-all duration-300 ${
                tier.featured
                  ? "bg-[#2C2B29] text-white border-[#2C2B29]"
                  : "bg-white border-[#E8E5DF]"
              }`}
            >
              {tier.featured && (
                <span className="absolute -top-3 left-6 inline-flex items-center bg-[#C96A4E] text-white rounded-full px-3 py-1 text-xs font-semibold">
                  Most popular
                </span>
              )}
              <h3
                className={`font-display text-2xl font-semibold leading-tight ${
                  tier.featured ? "text-white" : "text-[#2C2B29]"
                }`}
              >
                {tier.name}
              </h3>
              <p
                className={`mt-2 text-sm leading-relaxed ${
                  tier.featured ? "text-white/70" : "text-[#5E5C58]"
                }`}
              >
                {tier.desc}
              </p>
              <ul
                className={`mt-5 space-y-2.5 text-sm ${
                  tier.featured ? "text-white/85" : "text-[#5E5C58]"
                }`}
              >
                {tier.points.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <Check
                      size={16}
                      className={`mt-0.5 shrink-0 ${
                        tier.featured ? "text-[#E8B5A1]" : "text-[#8A9A92]"
                      }`}
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* GLP-1 PRICING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Weight loss program
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]">
            GLP-1 &amp; GLP-1/GIP Support
          </h2>
          <p className="mt-4 text-[#5E5C58] leading-relaxed max-w-2xl">
            Prescription GLP-1 and GIP care designed to support weight
            management as part of a clinician-guided plan. Available when
            medically appropriate after a comprehensive health review.
          </p>
        </div>

        <div
          data-testid="pricing-glp1-card"
          className="mt-8 bg-white rounded-3xl border border-[#E8E5DF] p-6 md:p-10"
        >
          <ul className="space-y-4">
            {GLP1_OPTIONS.map((o) => (
              <li
                key={o.name}
                data-testid={`pricing-glp1-option-${o.name
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 pb-4 border-b border-[#F0EDE7] last:border-b-0 last:pb-0"
              >
                <span className="text-[#8A9A92] text-xs sm:text-sm md:text-base uppercase tracking-[0.14em] font-medium">
                  {o.name}
                </span>
                <span className="flex flex-col sm:items-end">
                  <span className="font-display text-xl md:text-2xl font-semibold text-[#2C2B29] leading-tight">
                    <span className="text-[#C96A4E]">${o.price}</span>
                    <span>/month</span>
                  </span>
                  <span className="text-xs md:text-sm text-[#8A9A92] font-normal leading-snug">
                    3-month plan
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <p
            data-testid="pricing-glp1-flat-rate-note"
            className="mt-5 text-sm text-[#5E5C58] leading-relaxed"
          >
            {FLAT_RATE_NOTE}
          </p>
          <p className="mt-2 text-xs text-[#8A9A92] leading-snug">
            Prescription required. Not all patients qualify. Results vary.
            Compounded semaglutide and compounded tirzepatide are not
            FDA-approved.
          </p>
          <button
            data-testid="pricing-glp1-start-now"
            type="button"
            onClick={() => goToProductIntake("weight-loss")}
            className="mt-6 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-7 py-3 text-sm md:text-base font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
          >
            Start now
            <ArrowRight size={16} className="ml-2" />
          </button>
        </div>
      </section>

      {/* DETAILS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="bg-[#F3F1EC]/60 rounded-3xl p-6 md:p-10 border border-[#E8E5DF]">
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#2C2B29]">
            What&apos;s included
          </h2>
          <ul className="mt-6 grid md:grid-cols-2 gap-3 text-sm text-[#5E5C58]">
            {[
              "No insurance needed",
              "Clinical review required",
              "Pharmacy fulfillment when prescribed",
              "Shipping: Free",
              "Supplies: Included",
              "Prescription only if medically appropriate",
            ].map((p) => (
              <li
                key={p}
                className="flex items-start gap-2.5 bg-white rounded-2xl p-4 border border-[#E8E5DF]"
              >
                <Check size={16} className="mt-0.5 shrink-0 text-[#8A9A92]" />
                <span className="leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]">
          Ready to get started?
        </h2>
        <button
          data-testid="pricing-page-start-now-cta"
          className="mt-6 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-4 text-base font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
          type="button"
          onClick={goToIntake}
        >
          Start now
          <ArrowRight size={18} className="ml-2" />
        </button>
        <p className="mt-3 text-xs text-[#5E5C58]">
          No insurance needed. Prescription only if medically appropriate.
        </p>
      </section>
    </div>
  );
}
